import { useEffect, useState } from "react";
import { Loader2, Check } from "lucide-react";
import { activateAccount, activationSeen } from "@/lib/api";
import { AI_POINTS_REFRESH } from "@/components/AiPointsBadge";

export const ACTIVATION_HERO = "https://uploads.orevital.com/postcue/house/activation/ai-powerhouse-welcome.jpg";

export default function ActivationPopup({ recId, onActivated }) {
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [closing, setClosing] = useState(false);

  useEffect(() => { const t = setTimeout(() => setShown(true), 30); return () => clearTimeout(t); }, []);
  useEffect(() => { if (recId) activationSeen(recId).catch(() => {}); }, [recId]);

  async function activate() {
    setBusy(true); setError("");
    try {
      const r = await activateAccount(recId);
      setDone(r);
      window.dispatchEvent(new Event(AI_POINTS_REFRESH));
      setTimeout(() => { setClosing(true); setTimeout(() => onActivated?.(r), 650); }, 2600);
    } catch (e) {
      setError(e?.response?.data?.detail || "Activation didn't go through — please try again.");
    } finally { setBusy(false); }
  }

  return (
    <div className={`fixed inset-0 z-[90] flex items-center justify-center p-3 sm:p-6 transition-opacity duration-500 ${shown && !closing ? "opacity-100" : "opacity-0"}`} data-testid="activation-popup">
      <div className="absolute inset-0 bg-[#14254b]/55 backdrop-blur-[2px]" />
      <div role="dialog" aria-labelledby="activation-title" aria-modal="true"
        className={`relative w-full max-w-[1040px] max-h-[94vh] overflow-y-auto bg-white rounded-3xl shadow-[0_24px_80px_#3e24782b] transition-transform duration-500 ${shown && !closing ? "scale-100 translate-y-0" : "scale-95 translate-y-3"}`}>
        <img src={ACTIVATION_HERO} alt="Welcome to OreVital Performance Peptides – explore your marketing tools" className="block w-full h-auto" data-testid="activation-hero" />
        <div className="text-center px-4 sm:px-6 pt-6 pb-8 text-[#14254b]">
          {done ? (
            <div className="py-6" data-testid="activation-success">
              <div className="mx-auto h-16 w-16 rounded-full bg-[#f4eafe] border border-[#d9c2ff] flex items-center justify-center text-[#7534d9]"><Check size={34} strokeWidth={3} /></div>
              <h1 className="text-[clamp(22px,3.5vw,34px)] font-extrabold mt-4">You&apos;re Activated! Your {done.bonus} FREE AI Points Are Ready!</h1>
              <p className="text-[#55627a] text-base mt-2">
                {done.bonus_awarded ? `Your balance is now ${done.ai_points_balance} AI points.` : `Your welcome bonus was already applied — balance ${done.ai_points_balance} AI points.`}
                {(done.email_sent || done.sms_sent) && <> A confirmation is on its way by {[done.email_sent && "email", done.sms_sent && "text"].filter(Boolean).join(" and ")}.</>}
              </p>
            </div>
          ) : (
            <>
              <div className="text-[#7534d9] font-extrabold tracking-[2px] text-xs">FREE FOR ACTIVE OREVITAL PROFESSIONALS</div>
              <h1 id="activation-title" className="text-[clamp(23px,3.5vw,37px)] font-extrabold my-3 leading-tight">Welcome Back! We&apos;ve Missed You! 💜</h1>
              <p className="text-[17px] text-[#55627a] leading-relaxed max-w-[730px] mx-auto mb-3">It&apos;s been a while since your last order — and our AI Marketing Powerhouse™ is now <strong className="text-[#14254b]">FREE</strong> for active OreVital professionals.</p>
              <p className="text-[15px] text-[#55627a] leading-relaxed max-w-[730px] mx-auto mb-5">Unlock full access with <strong className="text-[#6526ba]">20 FREE AI Points</strong>: professional emails, personalized videos, social posts and branded commercials. As you return to ordering, every purchase keeps earning AI Points — more marketing content at no extra cost.</p>
              <div className="bg-[#f4eafe] border border-[#d9c2ff] rounded-2xl p-4 max-w-[690px] mx-auto mb-5 font-bold text-[#6526ba] text-[17px] sm:text-xl" data-testid="activation-gift">
                🎁 Activate today &amp; claim 20 FREE AI Points
              </div>
              <button type="button" onClick={activate} disabled={busy}
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#7534e9] to-[#a03ad9] text-white rounded-2xl px-6 py-4 text-base sm:text-lg font-extrabold shadow-[0_8px_22px_#8143c94d] hover:brightness-105 active:scale-[0.99] transition disabled:opacity-60 w-full sm:w-auto"
                data-testid="activation-cta">
                {busy ? <Loader2 size={18} className="animate-spin" /> : null}
                {busy ? "ACTIVATING…" : "ACTIVATE MY AI POWERHOUSE + CLAIM 20 FREE POINTS"}
              </button>
              {error && <div className="mt-4 text-sm font-semibold text-rose-600" role="alert" data-testid="activation-error">{error} <button type="button" onClick={activate} className="underline ml-1">Retry</button></div>}
              <p className="text-[#14254b] font-bold text-sm mt-4">Your Business. Your Brand. Your Comeback!</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
